import { Link } from "react-router-dom";
import { useState } from "react";
import { Share2, Sparkles, BookOpen, MessageCircle, Twitter, Activity, MessageCircleQuestion } from "lucide-react";
import VerifiedBadge from "./VerifiedBadge";
import PulseModal from "./PulseModal";
import AskReporterModal from "./AskReporterModal";
import PricingModal from "./PricingModal";

const CATEGORY_LABEL = {
  top: "TOP",
  government: "POLITICS",
  economy: "BUSINESS",
  markets: "MARKETS",
  money: "MONEY",
  realestate: "REAL ESTATE",
  family: "FAMILY",
  world: "WORLD",
  technology: "TECH",
  ai: "AI",
  health: "HEALTH",
  medicine: "MEDICINE",
  sports: "SPORTS",
  hollywood: "HOLLYWOOD",
  entertainment: "ENTERTAINMENT",
  culture: "CULTURE",
  voices: "VOICES",
  editorial: "EDITORIAL",
  local: "LOCAL",
};

const CAT_COLORS = {
  government: ["#0e2d63", "#1e3a8a", "#2563eb"],
  economy: ["#0a4d3a", "#065f46", "#10b981"],
  markets: ["#5b1a08", "#7c2d12", "#ea580c"],
  world: ["#0e2d63", "#1e40af", "#3b82f6"],
  technology: ["#3b0764", "#581c87", "#9333ea"],
  health: ["#083344", "#0e7490", "#06b6d4"],
  default: ["#0a1f3d", "#0e2d63", "#1e40af"],
};

const escapeXml = (s = "") =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const wrap = (text, perLine = 38, maxLines = 3) => {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > perLine) {
      if (cur) lines.push(cur.trim());
      cur = w;
      if (lines.length >= maxLines) break;
    } else {
      cur = (cur + " " + w).trim();
    }
  }
  if (cur && lines.length < maxLines) lines.push(cur.trim());
  if (words.length && lines.length === maxLines) {
    lines[maxLines - 1] = lines[maxLines - 1].replace(/[.,;:!?]?$/, "") + "…";
  }
  return lines;
};

const svgPlaceholder = ({ category = "default", label, headline = "Newseze", source = "" } = {}) => {
  const [c0, c1, c2] = CAT_COLORS[category] || CAT_COLORS.default;
  const lbl = String(label || CATEGORY_LABEL[category] || "NEWSEZE").toUpperCase().slice(0, 18);
  const lines = wrap(headline, 38, 3);
  const head = lines
    .map((l, i) => `<text x='60' y='${320 + i * 56}' font-size='44' font-weight='800' fill='rgba(255,255,255,0.97)' letter-spacing='-0.5'>${escapeXml(l)}</text>`)
    .join("");
  const src = source ? `<text x='60' y='620' font-size='14' font-weight='700' letter-spacing='3' fill='rgba(255,255,255,0.7)'>${escapeXml(source.toUpperCase().slice(0, 28))} · NEWSEZE</text>` : `<text x='60' y='620' font-size='14' font-weight='700' letter-spacing='3' fill='rgba(255,255,255,0.7)'>NEWSEZE · BALANCED NEWS</text>`;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 675' preserveAspectRatio='xMidYMid slice'>
    <defs>
      <linearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'>
        <stop offset='0%25' stop-color='${c0}'/>
        <stop offset='55%25' stop-color='${c1}'/>
        <stop offset='100%25' stop-color='${c2}'/>
      </linearGradient>
      <pattern id='dots' width='40' height='40' patternUnits='userSpaceOnUse'>
        <circle cx='1' cy='1' r='1' fill='rgba(255,255,255,0.06)'/>
      </pattern>
    </defs>
    <rect width='1200' height='675' fill='url(%23g)'/>
    <rect width='1200' height='675' fill='url(%23dots)'/>
    <rect x='60' y='210' width='6' height='80' fill='%23f5a524'/>
    <text x='90' y='268' font-size='15' font-weight='900' letter-spacing='5' fill='%23f5a524'>${escapeXml(lbl)}</text>
    <g font-family='Inter, Arial, sans-serif'>
      ${head}
      ${src}
    </g>
  </svg>`.replace(/\n\s+/g, " ");
  return `data:image/svg+xml;utf8,${svg.replace(/#/g, "%23")}`;
};

export function Img({ src, category, alt, headline, source }) {
  const placeholder = svgPlaceholder({ category, headline: headline || alt, source });
  return (
    <img
      src={src || placeholder}
      alt={alt}
      loading="lazy"
      onError={(e) => {
        if (e.currentTarget.src !== placeholder) e.currentTarget.src = placeholder;
      }}
    />
  );
}

export default function NewsCard({ article, onBrief, onShare }) {
  const [showPulse, setShowPulse] = useState(false);
  const [showAsk, setShowAsk] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  if (!article) return null;
  const cat = CATEGORY_LABEL[article.category] || article.category?.toUpperCase();
  const isOpinion = article.kind === "voice" || article.kind === "editorial";
  return (
    <article className="news-card" data-testid={`news-card-${article.id}`}>
      <Link to={`/article/${article.id}`} className="image">
        <Img src={article.image} category={article.category} alt={article.title} headline={article.title} source={article.source} />
      </Link>
      <div className="body">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="cat">{cat}</span>
          {isOpinion && (
            <span className="text-[9px] uppercase tracking-[0.16em] font-black text-[#1d1303] bg-[var(--gold)] px-1.5 py-0.5 rounded" data-testid={`opinion-tag-${article.id}`}>
              {article.kind === "voice" ? "Commentary" : "Editorial"}
            </span>
          )}
          {article.right_radar && (
            <span className="text-[9px] uppercase tracking-[0.16em] font-black text-white bg-rose-600 px-1.5 py-0.5 rounded" data-testid={`right-radar-${article.id}`} title="Trending on right-leaning wires today — Newseze covers the story in its own calm voice.">
              Trending Right
            </span>
          )}
          {/* Lean labels removed 2026-06-01 — Newseze stops categorizing stories
              by political tilt in the public UI. The editorial dial still runs
              silently server-side; readers see trust score + Newseze brand only.
              See PRD.md for rationale. */}
          {typeof article.trust === "number" && article.trust > 0 && (
            <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-[var(--blue)]" data-testid={`trust-tag-${article.id}`}>
              trust {article.trust}
            </span>
          )}
        </div>
        <Link to={`/article/${article.id}`} className="title" data-testid={`news-title-${article.id}`}>
          {article.title}
        </Link>
        {article.narrative && (
          <p className="text-[12.5px] text-[var(--ink)] leading-snug mt-1 border-l-2 border-[var(--gold)] pl-2" data-testid={`narrative-${article.id}`}>
            <span className="text-[9px] uppercase tracking-[0.16em] font-black text-[var(--gold)] mr-1.5">Why it matters</span>
            {article.narrative.slice(0, 220)}{article.narrative.length > 220 ? "…" : ""}
          </p>
        )}
        {article.brief_preview && article.brief_preview !== article.narrative && (
          <p className="summary mt-1" data-testid={`brief-${article.id}`}>
            {article.brief_preview.slice(0, 160)}{article.brief_preview.length > 160 ? "…" : ""}
          </p>
        )}
        <div className="meta">
          {article.avatar?.portrait && (
            <img src={article.avatar.portrait} alt={article.avatar.name} className="w-6 h-6 rounded-full inline-block mr-1 -ml-0.5" data-testid={`avatar-thumb-${article.id}`} />
          )}
          <span className="font-black text-[var(--ink)]">
            {article.avatar?.name ? `By ${article.avatar.name}` : "Newseze Wire"}
          </span>
          {article.avatar?.role && <span className="text-[var(--muted)] hidden sm:inline">· {article.avatar.role.split(' · ')[0]}</span>}
          <span>·</span>
          <span title={new Date(article.published).toLocaleString()}>{(() => {
            const pubDate = new Date(article.published);
            const diffMs = Date.now() - pubDate.getTime();
            const mins = Math.floor(diffMs / 60000);
            if (mins < 1) return "just now";
            if (mins < 60) return `${mins}m ago`;
            const hours = Math.floor(mins / 60);
            if (hours < 24) return `${hours}h ago`;
            const days = Math.floor(hours / 24);
            if (days < 7) return `${days}d ago`;
            return pubDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          })()}</span>
        </div>
        {/* Wire attribution — secondary, compact, compliance-friendly (Apple News / SmartNews model) */}
        {article.source && (
          <div className="text-[10px] text-[var(--muted)] mt-0.5 flex items-center gap-1" data-testid={`wire-${article.id}`}>
            <span className="uppercase tracking-[0.12em] font-bold opacity-70">Wire</span>
            <span className="opacity-90">{article.source}</span>
            <VerifiedBadge name={article.source} compact testId={`news-verified-${article.id}`} />
          </div>
        )}
        <div className="actions">
          <button onClick={() => setShowPulse(true)} className="btn icon" data-testid={`pulse-btn-${article.id}`} title="Explain this issue — personal & financial impact">
            <Activity size={12} /> Pulse
          </button>
          <button onClick={() => setShowAsk(true)} className="btn icon" data-testid={`ask-btn-${article.id}`} title="Questeze — ask the Newseze reporter about this story">
            <MessageCircleQuestion size={12} /> Questeze
          </button>
          <button onClick={() => onBrief?.(article)} className="btn icon" data-testid={`brief-btn-${article.id}`}>
            <Sparkles size={12} /> Brief me
          </button>
          <Link to={`/article/${article.id}#newseze-analysis`} className="btn icon" data-testid={`analysis-btn-${article.id}`}>
            <BookOpen size={12} /> Full Analysis
          </Link>
          <Link to={`/article/${article.id}#ask-us-section`} className="btn icon" data-testid={`comment-btn-${article.id}`} title="Ask Newseze about this story">
            <MessageCircle size={12} /> Comment
          </Link>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title + " via @Newseze")}&url=${encodeURIComponent(typeof window !== "undefined" ? `${window.location.origin}/article/${article.id}` : `/article/${article.id}`)}`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            referrerPolicy="no-referrer"
            className="btn icon"
            data-testid={`x-share-btn-${article.id}`}
            title="Post to X"
          >
            <Twitter size={12} /> Post
          </a>
          <button onClick={() => onShare?.(article)} className="btn icon" data-testid={`share-btn-${article.id}`}>
            <Share2 size={12} /> Share
          </button>
          <Link to={`/article/${article.id}`} className="btn icon ml-auto" data-testid={`read-btn-${article.id}`}>
            Read →
          </Link>
        </div>
      </div>
      {showPulse && (
        <PulseModal
          article={article}
          onClose={() => setShowPulse(false)}
          onUpgrade={() => { setShowPulse(false); setShowPricing(true); }}
        />
      )}
      {showAsk && (
        <AskReporterModal
          article={article}
          onClose={() => setShowAsk(false)}
          onUpgrade={() => { setShowAsk(false); setShowPricing(true); }}
        />
      )}
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} />}
    </article>
  );
}
